import { useRef } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Button } from "@mui/material";
import { Upload } from "lucide-react";
import { uploadFile } from "../../../../../helpers";
import { FormLabel, FTPInput } from "../../../../../components-new";
import type { SourceFile } from "../sources-types";
import { toParentIdNum, getTarsS3DirPath, TARS_SOURCES_BUCKET } from "./addSourceUtils";
import { formTitleClass, primaryButtonSx, cancelButtonSx, formActionsClass } from "./addSourceStyles";
import { useSourceForm } from "./useSourceForm";
import { SourceFormFooter } from "./SourceFormFooter";

interface UploadFileFormProps {
  parentId: string | null;
  editNode?: SourceFile | null;
  onSuccess: () => void;
  onCancel: () => void;
}

export default function UploadFileForm({ parentId, editNode, onSuccess, onCancel }: UploadFileFormProps) {
  const { isEdit, notebookIdNum, tarsStore, uploadSource, renameNode } = useSourceForm(parentId, editNode ?? null, onSuccess);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const methods = useForm<{ name: string }>({ defaultValues: { name: editNode?.name ?? "" } });

  const getDisplayName = (file: File, index: number): string => {
    const externalName = methods.getValues("name")?.trim();
    const ext = file.name.split(".").pop() ?? "file";
    if (!externalName) return file.name;
    return index === 0 ? `${externalName}.${ext}` : `${externalName} (${index + 1}).${ext}`;
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length || notebookIdNum == null) return;
    const parentIdNum = toParentIdNum(parentId);
    const s3DirPath = getTarsS3DirPath(notebookIdNum, tarsStore.getFolderPath(parentId));
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const s3Key = await uploadFile(s3DirPath, { file }, TARS_SOURCES_BUCKET);
        if (!s3Key) continue;
        const created = await uploadSource({
          notebook_id: notebookIdNum,
          type: "file",
          parent_id: parentIdNum,
          name: getDisplayName(file, i),
          s3_key: s3Key,
          size_bytes: file.size,
          mime_type: file.type || undefined,
          more: { extension: file.name.split(".").pop() ?? "file" },
        });
        tarsStore.addSourceFromServer(created, parentId);
      }
      e.target.value = "";
      onSuccess();
    } catch {
      // Error handled by useUploadSource
    }
  };

  const onSubmit = async (data: { name: string }) => {
    if (isEdit && editNode) await renameNode(data.name?.trim() || editNode.name);
  };

  if (isEdit && editNode) {
    return (
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <FormLabel className={formTitleClass}>Edit File</FormLabel>
          <FTPInput name="name" label="File name" className="mb-3" size="small" />
          <SourceFormFooter onCancel={onCancel} submitLabel="Save" />
        </form>
      </FormProvider>
    );
  }

  return (
    <FormProvider {...methods}>
      <FormLabel className={formTitleClass}>Upload File</FormLabel>
      <FTPInput name="name" label="Name of the file" className="mb-3" size="small" placeholder="Enter display name for the file" />
      <div className={formActionsClass}>
        <Button type="button" variant="outlined" onClick={onCancel} sx={cancelButtonSx}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => fileInputRef.current?.click()}
          startIcon={<Upload size={18} />}
          sx={primaryButtonSx}
        >
          Choose files
        </Button>
      </div>
      <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleChange} />
    </FormProvider>
  );
}
